import { createClient } from '@supabase/supabase-js';
import { Preferences } from '@capacitor/preferences';
import { isNative } from '../capacitor/platform';
import type { Database } from '../types/database.types';

// Validar que las variables de entorno estén configuradas
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error(
    'Faltan variables de entorno de Supabase. ' +
    'Asegúrate de configurar VITE_SUPABASE_URL y VITE_SUPABASE_ANON_KEY en tu archivo .env'
  );
}

// En iOS 17, WKWebView purga localStorage bajo presión de memoria, lo que provoca
// que el refresh token desaparezca y supabase-js emita SIGNED_OUT en mitad de una sesión.
// Capacitor Preferences usa NSUserDefaults en iOS y SharedPreferences en Android, ambos
// sobreviven a la limpieza del WebView. En web seguimos usando localStorage.
const nativeAuthStorage = {
  async getItem(key: string): Promise<string | null> {
    const { value } = await Preferences.get({ key });
    if (value !== null) return value;

    // Migración única: si el usuario ya estaba logueado con la versión anterior
    // (tokens en localStorage), los movemos a Preferences en el primer arranque
    // para que no se desautentique tras actualizar.
    try {
      const legacy = window.localStorage.getItem(key);
      if (legacy !== null) {
        await Preferences.set({ key, value: legacy });
        window.localStorage.removeItem(key);
        return legacy;
      }
    } catch {
      // localStorage puede no estar disponible; ignorar.
    }
    return null;
  },
  async setItem(key: string, value: string): Promise<void> {
    await Preferences.set({ key, value });
  },
  async removeItem(key: string): Promise<void> {
    await Preferences.remove({ key });
  },
};

// Crear cliente de Supabase con tipos generados automáticamente
export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    flowType: 'pkce',
    storage: isNative ? nativeAuthStorage : window.localStorage,
    storageKey: 'uni-app-auth',
  },
  realtime: {
    params: {
      eventsPerSecond: 10,
    },
  },
  db: {
    schema: 'public',
  },
});

// Helper para manejar errores de Supabase de forma consistente
export const handleSupabaseError = (error: any): never => {
  console.error('Supabase error:', error);

  if (error.message) {
    throw new Error(error.message);
  }

  throw new Error('Ha ocurrido un error inesperado');
};

// Helper para extraer datos de respuesta de Supabase con manejo de errores
export function unwrapData<T>(data: T | null, error: any, context?: string): T {
  if (error) {
    console.error(context ? `Error en ${context}:` : 'Supabase error:', error);

    const errorMsg = error.message || error.toString();

    // Detectar errores RLS específicos
    if (errorMsg.includes('row-level security') || errorMsg.includes('RLS')) {
      throw new Error('No tienes permisos para ver estos datos. Verifica que estés autenticado.');
    }

    // Detectar errores de autenticación
    if (errorMsg.includes('JWT') || errorMsg.includes('token') || errorMsg.includes('auth')) {
      throw new Error('Tu sesión ha expirado. Por favor, inicia sesión nuevamente.');
    }

    // Detectar errores de conexión
    if (errorMsg.includes('network') || errorMsg.includes('fetch') || errorMsg.includes('ECONNREFUSED')) {
      throw new Error('Error de conexión. Verifica tu conexión a internet e intenta nuevamente.');
    }

    // Error genérico con contexto
    const finalMsg = context
      ? `Error en ${context}: ${errorMsg}`
      : errorMsg;

    throw new Error(finalMsg);
  }

  if (data === null) {
    const nullMsg = context
      ? `No hay datos en ${context}`
      : 'No se encontraron datos';
    throw new Error(nullMsg);
  }

  return data;
}

// Helper para agregar timeout a promesas
export function withTimeout<T>(
  promise: PromiseLike<T>,
  timeoutMs: number,
  operationName: string = 'Operación'
): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) =>
      setTimeout(
        () => reject(new Error(`${operationName} excedió el tiempo límite de ${timeoutMs / 1000}s`)),
        timeoutMs
      )
    ),
  ]);
}

// Helper para verificar si hay una sesión activa
export const getSession = async () => {
  const { data, error } = await supabase.auth.getSession();

  if (error) {
    handleSupabaseError(error);
  }

  return data.session;
};

// Helper para obtener el usuario actual
export const getCurrentUser = async () => {
  const { data, error } = await supabase.auth.getUser();

  if (error) {
    handleSupabaseError(error);
  }

  return data.user;
};

// Helper para obtener el perfil completo del usuario
export const getUserProfile = async (userId?: string) => {
  const uid = userId || (await getCurrentUser())?.id;

  if (!uid) {
    throw new Error('No hay usuario autenticado');
  }

  try {
    const queryPromise = supabase
      .from('users')
      .select('*')
      .eq('id', uid)
      .single();

    // Timeout generoso (8s) para tolerar redes celulares lentas de iOS sin
    // dejar al usuario en estado "no autenticado" cuando la sesión sí es válida.
    const { data, error } = await withTimeout(queryPromise, 8000, 'Query getUserProfile');

    return unwrapData(data, error);
  } catch (error: any) {
    console.error('Error o timeout en getUserProfile():', error.message);
    throw error;
  }
};

// Types para facilitar el uso en toda la aplicación
export type SupabaseClient = typeof supabase;
export type { Database } from '../types/database.types';
